import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { formatUrl } from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { getContent } from '@plone/volto/actions/content/content';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import './password-gate.css';

/**
 * Registered as Volto's error view for HTTP 401. Most 401s are Volto's
 * normal "you must log in" case (private content, anonymous visitor) - for
 * those, delegate straight to Volto's own Unauthorized view, unchanged.
 * Only takes over when the 401 body is specifically our own
 * collective.folderprotection.volto JSON error shape
 * (type: "PasswordProtected"), rendering a password box instead of a
 * generic error page, and asking to unlock via
 * @password-protection instead of sending the visitor to /login - this is
 * a shared secret, not a real account.
 */
const PasswordGate = () => {
  const error = useSelector((state) => state.apierror?.error);
  const body = error?.response?.body;
  const dispatch = useDispatch();
  const location = useLocation();

  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!body || body.type !== 'PasswordProtected') {
    return <Unauthorized />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFailed(false);
    try {
      const response = await fetch(
        formatUrl(`${body.protected_path}/@password-protection`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ password }),
        },
      );
      if (response.ok) {
        // Deliberately NOT window.location.reload(): Volto's server-side
        // render only forwards a curated set of headers to its own backend
        // calls (see helpers/Proxy/Proxy.js) - our unlock cookie isn't one
        // of them, so a hard reload would hit the SSR pass cookie-less and
        // land right back on this same gate. A client-side re-fetch of the
        // same content is a real browser request, cookie included, so it
        // sees the unlock immediately. Re-verify this if Volto's SSR
        // cookie-forwarding ever changes.
        dispatch(getContent(flattenToAppURL(location.pathname)));
        return;
      }
      setFailed(true);
    } catch (e) {
      setFailed(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="password-gate">
      <div className="password-gate__box">
        <div className="password-gate__icon" aria-hidden="true">
          🔒
        </div>
        <h1>This content is password protected</h1>
        {body.reason?.data && (
          <div
            className="password-gate__reason"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: body.reason.data }}
          />
        )}
        <form className="password-gate__form" onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            autoFocus
            disabled={submitting}
          />
          <button type="submit" disabled={submitting || !password}>
            {submitting ? 'Checking…' : 'Unlock'}
          </button>
        </form>
        {failed && (
          <p className="password-gate__error" role="alert">
            That password isn&apos;t right - please try again.
          </p>
        )}
      </div>
    </div>
  );
};

export default PasswordGate;
